import React, { useState, useEffect } from "react";
import { ImageResponsive } from "../../../Image";
import { ImageFit } from "../../../Image/interface/ImageData";
import {
  SlideCard,
  SlideCta,
  SlideMedia,
  SlideOverlay,
} from "./VideoSlide.styled";
import Typography from "../../../../Foundation/Typography/Typography";
import StyledDiv from "../../../StyledDiv/StyledDiv";
import FlexBox from "../../../FlexBox/FlexBox";
import { responsiveMobileBreakpoint } from "../../../Grid/_utils/responsiveObserve";

const isAvailable = (availableDate?: string): boolean => {
  if (!availableDate) return true;
  return new Date(availableDate).getTime() <= Date.now();
};

const defaultFormatAvailableDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
  });

export interface IVideoSlideProps {
  title?: string;
  /** ex: "Épisode 1", "Épisode 2" */
  subtitle?: string;
  coverImage?: string;
  /** Video slug (SushishopVideo) */
  videoSlug?: string;
  /** ISO date string — si dans le futur, la vidéo n'est pas encore disponible */
  availableDate?: string;
  /** Injecté par l’app (ex. moment + locale). Sinon `defaultFormatAvailableDate`. */
  formatAvailableDateDisplay?: (dateIso: string) => string;
  isActive: boolean;
  watchNowLabel: string;
  newEpisodeLabel: string;
  renderVideo?: (videoSlug: string, onEnded: () => void) => React.ReactNode;
  /** Sur mobile : ouvre la vidéo dans le player natif en plein écran */
  onMobilePlay?: () => void;
}

const VideoSlide = (props: IVideoSlideProps) => {
  const {
    title,
    subtitle,
    coverImage,
    videoSlug,
    availableDate,
    formatAvailableDateDisplay,
    isActive,
    watchNowLabel,
    newEpisodeLabel,
    renderVideo,
    onMobilePlay,
  } = props;

  const [playing, setPlaying] = useState(false);
  const available = isAvailable(availableDate);

  // Pause video when slide is no longer active
  useEffect(() => {
    if (!isActive) {
      setPlaying(false);
    }
  }, [isActive]);

  const dateForCta = availableDate
    ? formatAvailableDateDisplay?.(availableDate) ||
      defaultFormatAvailableDate(availableDate)
    : "";

  const ctaLabel = available
    ? watchNowLabel
    : `${newEpisodeLabel} ${dateForCta}`;

  return (
    <SlideCard>
      <SlideMedia>
        {playing && videoSlug && renderVideo
          ? renderVideo(videoSlug, () => setPlaying(false))
          : coverImage && (
              <ImageResponsive
                alt={title || ""}
                desktop={{
                  image: {
                    id: coverImage,
                    fit: ImageFit.COVER,
                    quality: 80,
                    aspectRatio: 1342 / 751,
                    width: 1342,
                  },
                  imgStyle: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  },
                }}
                mobile={{
                  image: {
                    id: coverImage,
                    fit: ImageFit.COVER,
                    quality: 80,
                    aspectRatio: 329 / 384,
                    width: 390,
                  },
                  imgStyle: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  },
                }}
              />
            )}
      </SlideMedia>

      {!playing && (
        <SlideOverlay>
          <FlexBox
            style={{ marginTop: "auto" }}
            direction="column"
            align="center"
            gap={8}
          >
            {title && (
              <Typography align="center" variant="adriencachotBig">
                {title}
              </Typography>
            )}
            <SlideCta
              as={Typography}
              variant="adriencachotUI"
              available={available}
              onClick={() => {
                if (!available || !videoSlug) return;
                const isMobile =
                  typeof window !== "undefined" &&
                  window.matchMedia(responsiveMobileBreakpoint).matches;
                if (isMobile && onMobilePlay) {
                  onMobilePlay();
                } else {
                  setPlaying(true);
                }
              }}
            >
              {ctaLabel}
            </SlideCta>
          </FlexBox>
          <StyledDiv
            as={Typography}
            styles={{
              textAlign: "center",
              marginTop: "auto",
            }}
            variant="adriencachotUI2"
          >
            {subtitle}
          </StyledDiv>
        </SlideOverlay>
      )}
    </SlideCard>
  );
};

export default VideoSlide;
